/**
 * Downline Management and the Admin Panel: Move a person or an org, and
 * Deactivate or Delete an org choosing where its people go first.
 *
 * Drew, 2026-09-29: deactivating Meridian Team West first asks one question,
 * all of it or let me choose; "let me choose" is the placing screen (everyone
 * to one place or each separately, teams under it moved intact), and whatever
 * is not placed goes with the org. Deactivate is everyone's verb (Downline,
 * Admin Panel); Delete is the Admin Panel's, platform staff only, for test
 * junk. Server side: move_member, move_org (20260929000001), deactivate_org
 * and purge_branch (20260929000002). Every destination offered here comes
 * from the viewer's own tree, so it is somewhere they run; the server checks
 * it again.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { typeLabel, type OrgNode, type OrgNodeType } from '@/components/org/hierarchyTree';
import { moveMember, moveOrg } from '@/components/org/hierarchyLive';
import { deactivateOrg, purgeBranch, type BranchPlan } from '@/lib/deactivation';

type Role = 'agent' | 'org_admin';
const ORG_TYPES: OrgNodeType[] = ['imo', 'agency', 'team'];
/** Where an org may sit: an agency under an IMO, a team under an IMO or an agency. */
const PARENT_TYPES: Partial<Record<OrgNodeType, OrgNodeType[]>> = { agency: ['imo'], team: ['imo', 'agency'] };

const userIdOf = (n: OrgNode) => (n.id.startsWith('user-') ? n.id.slice(5) : n.id);

interface OrgOption { id: string; label: string }

/** Every org in the viewer's tree (never the platform itself), labelled with
 *  its place ("Meridian IMO › Dallas Agency"), minus `excludeUnder` and
 *  everything below it, limited to `types` when given. */
function orgDestinations(root: OrgNode, opts: { excludeUnder?: string; types?: OrgNodeType[] } = {}): OrgOption[] {
  const out: OrgOption[] = [];
  const walk = (n: OrgNode, trail: string[]) => {
    if (n.id === opts.excludeUnder || n.type === 'agent') return;
    const isOrg = ORG_TYPES.includes(n.type);
    const here = isOrg ? [...trail, n.name] : trail;
    if (isOrg && (!opts.types || opts.types.includes(n.type))) out.push({ id: n.id, label: here.join(' › ') });
    n.children.forEach(c => walk(c, here));
  };
  walk(root, []);
  return out;
}

/** The org a person sits in: as a leaf, or on an org's admins list. */
function orgOfPerson(root: OrgNode, personId: string): OrgNode | null {
  const walk = (n: OrgNode): OrgNode | null => {
    if (n.children.some(c => c.id === personId) || n.admins?.some(a => a.id === personId)) return n;
    for (const c of n.children) {
      const r = walk(c);
      if (r) return r;
    }
    return null;
  };
  return walk(root);
}

/** The people directly in an org, owner excluded, each once. */
function peopleIn(org: OrgNode): OrgNode[] {
  const seen = new Map<string, OrgNode>();
  for (const p of [...org.children.filter(c => c.type === 'agent'), ...(org.admins ?? [])]) {
    if (p.orgRole === 'org_owner') continue;
    if (!seen.has(p.id)) seen.set(p.id, p);
  }
  return [...seen.values()];
}

function OrgSelect({ value, onChange, options, placeholder = 'Choose…', label }: {
  value: string; onChange: (v: string) => void; options: OrgOption[]; placeholder?: string; label: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs" aria-label={label}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o.id} value={o.id} className="text-xs">{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function RoleSelect({ value, onChange, label }: { value: Role; onChange: (v: Role) => void; label: string }) {
  return (
    <Select value={value} onValueChange={v => onChange(v as Role)}>
      <SelectTrigger className="h-8 text-xs w-[96px]" aria-label={label}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="agent" className="text-xs">Agent</SelectItem>
        <SelectItem value="org_admin" className="text-xs">Admin</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ─── Move ──────────────────────────────────────────────────────────────────

export function MoveDialog({ root, node, onClose, onDone }: {
  root: OrgNode; node: OrgNode | null; onClose: () => void; onDone: () => void;
}) {
  const isPerson = node?.type === 'agent';
  const current = node ? (isPerson ? orgOfPerson(root, node.id) : null) : null;
  const options = useMemo(() => {
    if (!node) return [];
    if (isPerson) return orgDestinations(root).filter(o => o.id !== current?.id);
    return orgDestinations(root, { excludeUnder: node.id, types: PARENT_TYPES[node.type] ?? [] });
  }, [root, node, isPerson, current?.id]);
  const [dest, setDest] = useState('');
  const [role, setRole] = useState<Role>('agent');
  const [busy, setBusy] = useState(false);

  const close = () => { setDest(''); setRole('agent'); onClose(); };
  const submit = async () => {
    if (!node || !dest) return;
    setBusy(true);
    try {
      if (isPerson) await moveMember(userIdOf(node), dest, role);
      else await moveOrg(node.id, dest);
      toast.success(`${node.name} moved`, { description: `Now in ${options.find(o => o.id === dest)?.label ?? 'the new org'}.` });
      close();
      onDone();
    } catch (err) {
      toast.error('Move failed', { description: sanitizeError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!node} onOpenChange={o => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Move {node?.name}</DialogTitle>
          <DialogDescription>
            {isPerson
              ? `Their clients go with them.${current ? ` Now in ${current.name}.` : ''}`
              : `Everyone and everything under ${node?.name} moves with it.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Move to</Label>
            {options.length === 0
              ? <p className="text-xs text-muted-foreground">There's nowhere in your downline this can go.</p>
              : <OrgSelect value={dest} onChange={setDest} options={options} label="Move to" />}
          </div>
          {isPerson && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">As</Label>
              <RoleSelect value={role} onChange={setRole} label="Role" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={!dest || busy}>{busy ? 'Moving…' : 'Move'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Deactivate or delete a branch ──────────────────────────────────────────

export type BranchActionMode = 'deactivate' | 'delete';

const MODE_COPY: Record<BranchActionMode, { verb: string; doing: string; done: string; all: string; whole: string; leftover: string }> = {
  deactivate: {
    verb: 'Deactivate',
    doing: 'Deactivating…',
    done: 'deactivated',
    all: 'Everyone in it is signed out and gone from every screen right away. Their clients stay with them, out of sight. Platform staff can bring it all back from the Admin Panel.',
    whole: 'Deactivate all of it',
    leftover: 'goes dark with it',
  },
  delete: {
    verb: 'Delete',
    doing: 'Deleting…',
    done: 'deleted',
    all: 'Everyone in it, their sign-ins and their clients are wiped now. This can\'t be undone. On real data, deactivate instead.',
    whole: 'Delete all of it',
    leftover: 'is deleted with it',
  },
};

/** Step one asks "all of it, or let me choose?"; step two is either the typed
 *  name or the placing screen plus the typed name. `org` is a node from the
 *  viewer's tree; a node with no people and nothing under it skips step one. */
export function BranchActionDialog({ mode, root, org, onClose, onDone }: {
  mode: BranchActionMode; root: OrgNode; org: OrgNode | null; onClose: () => void; onDone: () => void;
}) {
  const copy = MODE_COPY[mode];
  // Parent lookup via the tree; the platform itself is never a destination.
  const parent = useMemo(() => {
    if (!org) return null;
    const walk = (n: OrgNode): OrgNode | null => {
      if (n.children.some(c => c.id === org.id)) return n;
      for (const c of n.children) { const r = walk(c); if (r) return r; }
      return null;
    };
    return walk(root);
  }, [root, org]);
  const defaultDest = parent && ORG_TYPES.includes(parent.type) ? parent.id : '';

  const people = useMemo(() => (org ? peopleIn(org) : []), [org]);
  const subOrgs = useMemo(() => (org ? org.children.filter(c => c.type !== 'agent') : []), [org]);
  const ownerLeaf = org?.children.find(c => c.type === 'agent' && c.orgRole === 'org_owner');
  const personDests = useMemo(() => (org ? orgDestinations(root, { excludeUnder: org.id }) : []), [root, org]);
  const canChoose = people.length + subOrgs.length > 0 && personDests.length > 0;

  const [step, setStep] = useState<'ask' | 'all' | 'choose'>('ask');
  const [everyone, setEveryone] = useState('');
  const [everyoneRole, setEveryoneRole] = useState<Role>('agent');
  const [separately, setSeparately] = useState(false);
  const [each, setEach] = useState<Record<string, string>>({});
  const [orgDest, setOrgDest] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const everyoneTo = everyone || defaultDest;
  const destFor = (p: OrgNode) => (separately ? each[p.id] || everyoneTo : everyoneTo);
  const subOrgTo = (o: OrgNode) => orgDest[o.id] ?? (parent && (PARENT_TYPES[o.type] ?? []).includes(parent.type) ? parent.id : '');

  const missing = step === 'choose' && (people.some(p => !destFor(p)) || subOrgs.some(o => !subOrgTo(o)));
  const typed = !!org && confirm.trim().toLowerCase() === org.name.trim().toLowerCase();
  const effectiveStep = step === 'ask' && !canChoose ? 'all' : step;

  const reset = () => {
    setStep('ask'); setEveryone(''); setEveryoneRole('agent'); setSeparately(false); setEach({}); setOrgDest({}); setConfirm('');
  };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!org || missing || !typed) return;
    const plan: BranchPlan | null = effectiveStep === 'choose' ? {
      people: people.map(p => ({ user_id: userIdOf(p), org_id: destFor(p), role: everyoneRole })),
      orgs: subOrgs.map(o => ({ org_id: o.id, parent_id: subOrgTo(o) })),
    } : null;
    setBusy(true);
    try {
      if (mode === 'deactivate') {
        const n = await deactivateOrg(org.id, plan);
        toast.success(`${org.name} deactivated`, {
          description: `${n} ${n === 1 ? 'person' : 'people'} went with it.${plan ? ' Everyone else is where you placed them.' : ''} Bring it back from the Admin Panel.`,
        });
      } else {
        const r = await purgeBranch(org.id, plan);
        toast.success(`${org.name} deleted`, {
          description: `${r.orgs} ${r.orgs === 1 ? 'organization' : 'organizations'} and ${r.people} ${r.people === 1 ? 'person' : 'people'} wiped.${plan ? ' Everyone else is where you placed them.' : ''}`,
        });
      }
      close();
      onDone();
    } catch (err) {
      toast.error(`${copy.verb} failed`, { description: sanitizeError(err) });
    } finally {
      setBusy(false);
    }
  };

  const owner = org?.admin || 'the owner';
  const kind = org ? typeLabel(org.type) : '';
  const countLine = [
    people.length > 0 ? `${people.length} ${people.length === 1 ? 'person' : 'people'}` : null,
    subOrgs.length > 0 ? `${subOrgs.length} ${subOrgs.length === 1 ? 'org' : 'orgs'} under it` : null,
  ].filter(Boolean).join(', ');

  return (
    <Dialog open={!!org} onOpenChange={o => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{copy.verb} {org?.name}</DialogTitle>
          <DialogDescription>
            {effectiveStep === 'choose'
              ? `Place anyone who should stay. Whoever is left, and ${owner}, ${copy.leftover}. Clients always go with their person.`
              : copy.all}
          </DialogDescription>
        </DialogHeader>

        {effectiveStep === 'ask' && (
          <div className="space-y-2 py-1">
            <button type="button" onClick={() => setStep('all')}
              className="w-full text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors">
              <span className="block text-sm font-semibold">{copy.whole}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{owner}, {countLine}: all of it, together.</span>
            </button>
            <button type="button" onClick={() => setStep('choose')}
              className="w-full text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors">
              <span className="block text-sm font-semibold">Let me choose</span>
              <span className="block text-xs text-muted-foreground mt-0.5">Move some people or teams somewhere else in your downline first. Whatever's left goes with {org?.name}.</span>
            </button>
          </div>
        )}

        {effectiveStep !== 'ask' && (
          <div className="space-y-5 py-1">
            {effectiveStep === 'all' && (people.length > 0 || subOrgs.length > 0 || ownerLeaf) && (
              <p className="text-xs text-muted-foreground">
                Going with it: {owner}{ownerLeaf ? ` (${ownerLeaf.clientCount} client${ownerLeaf.clientCount === 1 ? '' : 's'})` : ''}{countLine ? `, ${countLine}` : ''}.
              </p>
            )}

            {effectiveStep === 'choose' && people.length > 0 && (
              <section className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">People ({people.length})</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs">{separately ? 'Anyone not set below goes to' : 'Move everyone to'}</span>
                  <div className="min-w-[220px] flex-1">
                    <OrgSelect value={everyoneTo} onChange={setEveryone} options={personDests} label="Move everyone to" />
                  </div>
                  <span className="text-xs">as</span>
                  <RoleSelect value={everyoneRole} onChange={setEveryoneRole} label="Everyone's role" />
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={separately} onCheckedChange={v => setSeparately(v === true)} aria-label="Assign each person separately" />
                  Assign each person separately
                </label>
                {separately && (
                  <div className="space-y-1.5 pl-1">
                    {people.map(p => (
                      <div key={p.id} className="flex items-center gap-2">
                        <span className="text-xs w-[150px] truncate" title={p.name}>{p.name}
                          <span className="text-muted-foreground"> · {p.clientCount} client{p.clientCount === 1 ? '' : 's'}</span>
                        </span>
                        <div className="flex-1">
                          <OrgSelect value={each[p.id] || everyoneTo} onChange={v => setEach(e => ({ ...e, [p.id]: v }))}
                            options={personDests} label={`Move ${p.name} to`} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {effectiveStep === 'choose' && subOrgs.length > 0 && (
              <section className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Under it ({subOrgs.length})</p>
                {subOrgs.map(o => (
                  <div key={o.id} className="flex items-center gap-2">
                    <span className="text-xs w-[150px] truncate" title={o.name}>{o.name} <span className="text-muted-foreground">· {typeLabel(o.type)}</span></span>
                    <span className="text-xs text-muted-foreground">moves to</span>
                    <div className="flex-1">
                      <OrgSelect value={subOrgTo(o)} onChange={v => setOrgDest(d => ({ ...d, [o.id]: v }))}
                        options={orgDestinations(root, { excludeUnder: org!.id, types: PARENT_TYPES[o.type] ?? [] })}
                        label={`Move ${o.name} to`} />
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">Each moves intact, with everyone in it.</p>
              </section>
            )}

            {effectiveStep === 'choose' && (
              <p className="text-xs text-muted-foreground">
                {owner}{ownerLeaf ? ` (${ownerLeaf.clientCount} client${ownerLeaf.clientCount === 1 ? '' : 's'})` : ''} {copy.leftover}: an owner goes with their {kind.toLowerCase()}.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="branch-action-confirm" className="text-xs">Type its name to confirm</Label>
              <Input id="branch-action-confirm" value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder={org?.name} className="font-mono text-sm" autoComplete="off" />
            </div>
            {missing && <p className="text-[11px] text-muted-foreground">Pick a destination for everyone above to continue.</p>}
          </div>
        )}

        <DialogFooter>
          {effectiveStep !== 'ask' && canChoose && (
            <Button variant="ghost" onClick={() => { setStep('ask'); setConfirm(''); }} className="mr-auto">Back</Button>
          )}
          <Button variant="outline" onClick={close}>Cancel</Button>
          {effectiveStep !== 'ask' && (
            <Button variant="destructive" onClick={submit} disabled={missing || !typed || busy}>
              {busy ? copy.doing : `${copy.verb} ${kind}`.trim()}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
